/**
 * Zod schemas + DTO types for the Section API surface — Block 2 «Трекер
 * объектов», Этап 01a. A Section is a «раздел объекта» (child of Project);
 * WorkloadFact.sectionId attributes отработанные часы to it («сотрудник —
 * объект — раздел — дата»).
 *
 * RBAC lives in the service (managerial-tier: ADMIN | ГИП / acts-as-GIP | РО of
 * the project). All mutating schemas are `.strict()` so `projectId` /
 * `createdById` smuggled in the body are rejected — they come from the URL / JWT.
 *
 * Имя раздела клиент НЕ присылает ни в одном запросе (KD-5 отменён заказчиком
 * 05.08.2026): при создании его берут из выбранного пункта справочника, а
 * переименования раздела внутри проекта не существует.
 *
 * M3: `SectionResponseSchema` carries `assignedToMe` (for the fact-modal picker,
 * a self-report surface) AND `assignedUserIds` (for the project-page management
 * tab) so `GET /projects/:id/sections` is the single source both FE surfaces read.
 *
 * ⚠️ ЧТО ЗНАЧИТ «НАЗНАЧЕН» — СМЕНИЛОСЬ, А ПОЛЯ ОСТАЛИСЬ (Ф3 · В2d). До этой
 * волны источником была строка `SectionAssignment`; таблица снесена миграцией
 * 052, и оба поля считаются наличием проектной РАБОТЫ (`Work`,
 * `kind = 'PROJECT_WORK'`) на паре «раздел × исполнитель» — тем же предикатом,
 * каким сервер решает, принять ли часы по разделу. Поля контракта не менялись
 * намеренно: их читают окно отчёта о часах и вкладка команды, и переименование
 * ради «чистоты» стоило бы двух согласованных правок ради нуля пользы.
 */
import { z } from 'zod';

// ─── Shared primitives ────────────────────────────────────────────────────

const UuidSchema = z.string().uuid();

/** ISO-8601 datetime (createdAt / updatedAt / archivedAt). */
const IsoDateTimeSchema = z.string().datetime({ offset: true });

// Схемы имени раздела здесь НЕТ. Свободное имя раздела было нужно ровно одному
// пути — переименованию внутри проекта; решение заказчика от 05.08.2026
// (`DECISIONS.md`, «Отменено заказчиком», KD-5) убрало этот путь целиком вместе
// с маршрутом `PATCH /sections/:id`. Имя раздела больше НЕ приходит от клиента
// ни в одном запросе: при создании сервер берёт его из выбранного пункта
// справочника, а на экран отдаёт актуальное имя пункта.

/** Sort order — non-negative int, capped well below Int4 to avoid abuse. */
const SortOrderSchema = z.number().int().min(0).max(100_000);

// ─── Write requests ───────────────────────────────────────────────────────

/**
 * `POST /projects/:projectId/sections`. BREAKING (Block 2 · Этап 2 · W2a): a
 * project section is now PICKED from the §5.2 catalog — the body carries a
 * `catalogItemId`, NOT a free-text `name`. The service looks up the
 * `SectionCatalogItem` (must EXIST and be ACTIVE, else 422 `CATALOG_ITEM_INVALID`)
 * and SNAPSHOTS its `name` into `Section.name` (KD-5). `projectId` comes from
 * the URL, `createdById` from the JWT — both deliberately absent from the body.
 * `sortOrder` defaults to 0 server-side if omitted.
 */
export const CreateSectionRequestSchema = z
  .object({
    catalogItemId: UuidSchema,
    sortOrder: SortOrderSchema.optional(),
  })
  .strict();
export type CreateSectionRequest = z.infer<typeof CreateSectionRequestSchema>;

// `UpdateSectionRequestSchema` удалена вместе с маршрутом `PATCH /sections/:id`
// (KD-5 отменён заказчиком 05.08.2026). Она описывала два поля: `name` —
// отменённое переименование, и `sortOrder` — перестановку, у которой не было и
// нет места вызова в интерфейсе. Схема без маршрута выглядит рабочим контрактом
// и однажды им становится, поэтому снята целиком, а не урезана до одного поля.

// `AssignSectionRequestSchema` удалена вместе с маршрутами `POST /sections/:id/
// assignments` и `DELETE /sections/:id/assignments/:userId` (Ф3 · В2d). Назначение
// на раздел — это теперь проектная РАБОТА: заводится через команду объекта и
// карточку работы, и своей схемы запроса на «назначить» не существует. Снята
// целиком по тому же доводу, что и `UpdateSectionRequestSchema` выше: схема без
// маршрута выглядит рабочим контрактом и однажды им становится.

// ─── Response shapes ──────────────────────────────────────────────────────

/**
 * A single Section as returned by the API. `assignedToMe` / `assignedUserIds`
 * (M3) are populated by the list endpoint so the FE picker (self) and the
 * management tab (roster) share one response.
 */
export const SectionResponseSchema = z.object({
  id: UuidSchema,
  projectId: UuidSchema,
  /**
   * Имя для показа. Это АКТУАЛЬНОЕ имя связанного пункта справочника §5.2, а не
   * колонка `Section.name` (KD-5 отменён заказчиком 05.08.2026). Переименовал
   * супервайзер пункт — здесь новое имя, без действий пользователя и без
   * расхождений между экранами. Колонка отдаётся сюда ТОЛЬКО когда связи с
   * каталогом нет (`catalogItemId` пуст) — иначе раздел остался бы безымянным.
   * Собирается сервером, `apps/api/src/section/section-display-name.ts`.
   */
  name: z.string(),
  /**
   * Идентификатор пункта справочника §5.2, из которого раздел заведён. NULL для
   * доэтапных разделов со свободным именем и для разделов, чей пункт каталога
   * был физически удалён (`onDelete: SetNull`) — ровно тот случай, когда `name`
   * выше приходит из колонки. Веб читает это поле, чтобы СКРЫТЬ уже добавленные
   * пункты в пикере «Добавить раздел» (SO-5).
   */
  catalogItemId: UuidSchema.nullable(),
  /**
   * Имя ГРУППЫ справочника §5.2, под которой лежит раздел (Ф3 · В3, T3.6,
   * долг #46). По нему карточка объекта строит верхний уровень дерева
   * «группа → раздел → работы».
   *
   * ⚠️ `null` — ЗАКОННОЕ И ЧАСТОЕ состояние, а не сбой, и означает оно ровно
   * «узла-родителя нет». Так приходят ТРИ разных раздела, и различать их
   * экрану не нужно: доэтапный со свободным именем (`catalogItemId` пуст),
   * раздел с оборванной связью (`onDelete: SetNull`) и раздел, заведённый из
   * пункта ВЕРХНЕГО уровня справочника — такой пункт сам себе группа и
   * родителя не имеет. Все три обязаны попасть в ЯВНЫЙ узел «Без группы» и
   * не исчезнуть; это критерий готовности волны, а не оформление.
   *
   * Поле ОБЯЗАТЕЛЬНОЕ, а не `.optional()`: «сервер не прислал» и «группы нет»
   * не должны сливаться в одно состояние. На сервере то же правило держит тип
   * `SectionGroupSource` (`section-display-name.ts`) — чтение, забывшее
   * расширить `select`, краснеет на сборке, а не отдаёт `null` молча.
   */
  groupName: z.string().nullable(),
  /**
   * Идентификатор группы справочника; `null` — группы нет (Ф3 · В3, T3.6,
   * правка П-8 круга ревью).
   *
   * ⚠️ ГРУППИРОВКА И КЛЮЧ РАСКРЫТИЯ В АДРЕСЕ СТРОЯТСЯ НА НЁМ, А НЕ НА ИМЕНИ.
   * По имени ломаются оба, и оба тихо: переименование группы супервайзером
   * обесценивает разосланные ссылки «открыть раздел X», а частичный уникальный
   * индекс на имена групп действует только на ЖИВЫХ узлах — значит архивная
   * группа-тёзка законна и сливалась бы с живой в один узел дерева.
   * `groupName` остаётся тем, что читает человек.
   */
  groupId: UuidSchema.nullable(),
  sortOrder: z.number().int(),
  version: z.number().int().nonnegative(),
  archivedAt: IsoDateTimeSchema.nullable(),
  createdById: UuidSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  /** Whether the CURRENT actor is assigned to this section (fact-modal picker).
   *  Always present, for every reader. */
  assignedToMe: z.boolean(),
  /**
   * All assigned user ids (project-page management tab). MANAGERIAL-ONLY (FP-4):
   * the list endpoint OMITS this field for non-managerial readers (a plain
   * participant EMPLOYEE gets only `assignedToMe`). Optional here so the omission
   * is a valid response shape.
   *
   * ⚠️ Прежняя приписка «present on create/update (managerial) responses» СНЯТА
   * как неверная: `POST /projects/:id/sections` состав не считает и поле не
   * несёт, а маршрута `PATCH` нет с 05.08.2026. Заполняет его РОВНО список.
   */
  assignedUserIds: z.array(UuidSchema).optional(),
});
export type SectionResponse = z.infer<typeof SectionResponseSchema>;

/** `GET /projects/:projectId/sections` response — the section list. */
export const SectionListResponseSchema = z.object({
  data: z.array(SectionResponseSchema),
});
export type SectionListResponse = z.infer<typeof SectionListResponseSchema>;

// `SectionAssignmentResponseSchema` удалена: модель снесена (Ф3 · В2d), и
// отвечать этой формой некому.
